/**
 * Client-side image compression for camera/gallery uploads.
 *
 * Modern phone cameras (e.g. OnePlus 10 Pro, iPhone 12) produce photos well
 * above the server's 3 MB limit by default, which makes the штатний "take a
 * photo → send" flow impossible. We downscale and re-encode in the browser
 * before upload so a freshly-taken photo reliably fits the limit.
 *
 * Uses native `createImageBitmap` + `canvas.toBlob` — no dependencies. On iOS
 * Safari this also decodes HEIC frames from the camera into a JPEG output.
 */

/** Largest side (px) the output is downscaled to. Keeps detail while shrinking. */
const MAX_DIMENSION = 1920

/** Target output size. Comfortably under the server's 3 MB hard limit. */
const TARGET_SIZE_BYTES = 2 * 1024 * 1024

/** JPEG quality search bounds — we step down until the blob fits the target. */
const QUALITY_START = 0.85
const QUALITY_MIN = 0.5
const QUALITY_STEP = 0.1

const drawToCanvas = (bitmap: ImageBitmap): HTMLCanvasElement => {
    const { width, height } = bitmap
    const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(width * scale)
    canvas.height = Math.round(height * scale)
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('2d context unavailable')
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    return canvas
}

const canvasToBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> =>
    new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality))

/**
 * Compress an image file so it fits comfortably under the upload limit.
 *
 * Downscales the largest side to {@link MAX_DIMENSION} and re-encodes to JPEG,
 * lowering quality stepwise until the result is <= {@link TARGET_SIZE_BYTES}.
 *
 * If the file is not an image, already small enough at native dimensions, or
 * decoding fails, the original file is returned unchanged — the caller's final
 * validation remains the safety net.
 */
export const compressImage = async (file: File): Promise<File> => {
    if (!file.type.startsWith('image/')) return file

    let bitmap: ImageBitmap
    try {
        bitmap = await createImageBitmap(file)
    } catch {
        // Unsupported/corrupt frame — leave it to the validation/server check.
        return file
    }

    try {
        const needsResize = Math.max(bitmap.width, bitmap.height) > MAX_DIMENSION
        // A small file that doesn't need resizing is already fine — avoid a
        // needless re-encode that could even grow a well-compressed JPEG.
        if (!needsResize && file.size <= TARGET_SIZE_BYTES) return file

        const canvas = drawToCanvas(bitmap)

        let best: Blob | null = null
        for (let quality = QUALITY_START; quality >= QUALITY_MIN; quality -= QUALITY_STEP) {
            const blob = await canvasToBlob(canvas, Number(quality.toFixed(2)))
            if (!blob) break
            best = blob
            if (blob.size <= TARGET_SIZE_BYTES) break
        }

        // Couldn't encode at all, or the smallest re-encode is still larger than
        // the source: keep the original and let validation/server decide.
        if (!best || best.size >= file.size) return file

        const newName = file.name.replace(/\.[^.]+$/, '') + '.jpg'
        return new File([best], newName, { type: 'image/jpeg', lastModified: file.lastModified })
    } finally {
        bitmap.close()
    }
}
